// ============================================================================
// contactService.ts — ЕДИНЫЙ сервис контактов для UI ассистента (SSoT/SRP).
//
// Зачем отдельный слой: UI (карточка/панель) НЕ вызывает RPC напрямую.
// Все источники контактов сведены в один каталог:
//   • entities        — сама запись человека/внешнего контакта;
//   • adapter_links   — РЕАЛЬНЫЕ внешние идентичности (Telegram/MAX/TDLib):
//                       только они обеспечивают доставку мостом базы;
//   • messenger       — внутренний чат (всегда доступен).
//
// «Написать в Telegram/MAX» = открыть прямой чат платформы с контактом:
// исходящее уходит во внешний транспорт МОСТОМ базы — и только если у контакта
// есть РЕАЛЬНАЯ привязка adapter_links. Канал, указанный в профиле (username),
// НЕ является подключением и в панели контактов не показывается.
// Отдельного RPC «отправить через адаптер» нет — UI не изобретает обходные пути
// (отправка через адаптеры — серверный механизм ассистента/моста).
//
// Дедупликация и поиск — чистые функции directory.ts; каналы — channels.ts.
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { createConversation, listConversations } from '@/lib/chatBridge';
import { friendlyError, logSwallowed } from '@/lib/errors';
import { addChannel, adapterChannel, channelLabel, internalChannel, mergeChannels } from './channels';
import { buildContactDirectory, contactMatchesQuery } from './directory';
import type { ContactAction, ContactChannel, ContactChannelKind, ContactDetails, ContactSummary } from './types';

export { contactMatchesQuery };

/** Логин системного бота «Мой ассистент» (создаётся ядром на каждую базу). */
export const ASSISTANT_BOT_LOGIN = 'assistant_bot';

/**
 * Чат ассистента — прямой чат с системным ботом. Проверяем по СЛУЖЕБНОМУ
 * логину (стабилен при переименовании), без догадок по заголовку.
 */
export function isAssistantBotLogin(login: string): boolean {
  const value = String(login || '').trim().toLowerCase();
  return value === ASSISTANT_BOT_LOGIN || value.startsWith('assistant_');
}

function text(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

/** Кэш «id сущности → профиль»: распознавание бота и логин в шапке чата. */
export interface EntityProfile {
  login: string;
  kind: string;
  isBot: boolean;
}

const entityProfileCache = new Map<string, EntityProfile>();

/**
 * Профиль сущности по id (единый сервисный доступ для чата). Используется,
 * когда серверный список людей скрывает бота (обычный пользователь), а
 * диалог уже открыт: логин/вид — стабильный признак бота и системного
 * ассистента. Кэш на сеанс — повторные рендеры не дублируют запрос.
 */
export async function resolveEntityProfile(id: string): Promise<EntityProfile> {
  const key = String(id || '').trim();
  if (!key) return { login: '', kind: '', isBot: false };
  const cached = entityProfileCache.get(key);
  if (cached) return cached;
  const entity = await getSDK().entity.getEntity({ id: key });
  const profile: EntityProfile = {
    login: text(entity?.login),
    kind: text(entity?.kind).toLowerCase(),
    isBot: !!entity?.isBot,
  };
  entityProfileCache.set(key, profile);
  return profile;
}

/** Логин сущности по id (обёртка над профилем — используется в шапке чата). */
export async function resolveEntityLogin(id: string): Promise<string> {
  return (await resolveEntityProfile(id)).login;
}

function flag(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';
}

function isTechnical(raw: Record<string, unknown>, login: string): boolean {
  const createdVia = text(raw.createdVia ?? raw.created_via).toLowerCase();
  return createdVia === 'adapter' || login.toLowerCase().startsWith('tg_');
}

function toSummary(raw: Record<string, unknown>, myId: string): ContactSummary | null {
  const id = text(raw.id);
  if (!id || id === myId) return null;
  if (flag(raw.isSystem ?? raw.is_system)) return null;
  const kind = text(raw.kind).toLowerCase();
  if (kind === 'bot' || kind === 'persona') return null;
  if (flag(raw.isBot ?? raw.is_bot)) return null;

  const login = text(raw.login);
  const technical = isTechnical(raw, login);
  // Заблокированные люди не предлагаются для переписки. Технические контакты
  // адаптеров создаются неактивными намеренно — их показываем.
  const active = raw.isActive ?? raw.is_active;
  if (!technical && active !== undefined && !flag(active)) return null;

  const name = text(raw.name) || login || id;
  const channels: ContactChannel[] = [];
  // Технический контакт создаётся приёмом Telegram с логином tg_{внешний id}
  // (SSoT ядра — metadata.ContactLogin). Для обычного пользователя привязки
  // adapter_links недоступны, но САМ контакт — доказательство работающего
  // моста: показываем Telegram-канал из чата, иначе человек не может начать
  // переписку, хотя сообщение реально уходит в Telegram. Админу этот канал
  // заменит РЕАЛЬНАЯ привязка (adapterId/adapterName) в loadContacts.
  const lowerLogin = login.toLowerCase();
  if (technical && lowerLogin.startsWith('tg_')) {
    const externalId = login.slice(3).trim();
    if (externalId) {
      channels.push({
        kind: 'telegram',
        source: 'adapter',
        label: channelLabel('telegram'),
        value: externalId,
        verified: true,
        adapterName: 'Telegram (из чата)',
      });
    }
  }
  return {
    id,
    name,
    login,
    phone: text(raw.phone),
    email: text(raw.email),
    city: text(raw.city),
    avatarUrl: text(raw.avatarUrl ?? raw.avatar_url),
    kind,
    technical,
    channels,
  };
}

interface DirectoryOptions {
  myId: string;
  /** Админ видит adapter_links всех контактов; обычный пользователь — только свои. */
  canReadAdapterLinks: boolean;
}

/**
 * Каталог контактов пользователя. Список сущностей — SSoT сервера:
 * не-админ получает только людей (серверная видимость), админ — всех,
 * клиент дополнительно исключает системные учётки и ботов. Участники личных
 * диалогов добавляются вторым источником: серверная видимость скрывает
 * технические контакты транспортов, но человек с ними переписывается.
 */
export async function loadContacts(opts: DirectoryOptions): Promise<ContactSummary[]> {
  const sdk = getSDK();
  const response = await sdk.entity.listEntities({});
  const rows = ((response?.items || []) as Record<string, unknown>[]);

  const byId = new Map<string, ContactSummary>();
  for (const row of rows) {
    const summary = toSummary(row, opts.myId);
    if (summary) byId.set(summary.id, summary);
  }

  let conversations: Record<string, unknown>[] = [];
  try {
    conversations = (await listConversations()) as Record<string, unknown>[];
  } catch (error) {
    // Без списка диалогов каталог всё равно работает (люди из listEntities).
    logSwallowed(error, 'lib/contacts/contactService.ts:directory-conversations');
  }

  for (const conversation of conversations) {
    if (text(conversation.kind) !== 'direct') continue;
    const members = (conversation.memberIds ?? conversation.member_ids) as unknown;
    if (!Array.isArray(members) || members.length !== 2) continue;
    const peerId = members.map(text).find((id) => id && id !== opts.myId);
    if (!peerId || byId.has(peerId)) continue;
    try {
      const entity = await sdk.entity.getEntity({ id: peerId });
      const summary = toSummary((entity || {}) as Record<string, unknown>, opts.myId);
      if (summary) byId.set(summary.id, summary);
    } catch (error) {
      // Удалённый/недоступный контакт диалога: список не ломаем, факт — в журнал.
      logSwallowed(error, 'lib/contacts/contactService.ts:directory-peer');
    }
  }

  // Внешние идентичности: у админа — одним запросом (эффективно), у обычного
  // пользователя их всё равно не отдадут для чужих сущностей.
  const linksByEntity = new Map<string, Record<string, unknown>[]>();
  if (opts.canReadAdapterLinks) {
    try {
      const linksResponse = await sdk.adapter.listAdapterLinks({});
      for (const link of ((linksResponse?.items || []) as Record<string, unknown>[])) {
        const entityId = text(link.entityId ?? link.entity_id);
        if (!entityId) continue; // ссылки контейнеров (групп) — не контакты
        const list = linksByEntity.get(entityId) || [];
        list.push(link);
        linksByEntity.set(entityId, list);
      }
    } catch (error) {
      // Внешние подключения — дополнение к каталогу: без них список контактов
      // остаётся рабочим (карточка покажет причину при открытии).
      logSwallowed(error, 'lib/contacts/contactService.ts:directory-links');
    }
  }

  const entries = Array.from(byId.values()).map((summary) => {
    const links = linksByEntity.get(summary.id) || [];
    for (const link of links) {
      const channel = adapterChannel(link);
      if (channel) addChannel(summary.channels, channel);
    }
    return { summary, links };
  });
  return buildContactDirectory(entries);
}

/**
 * Полная карточка контакта: профильные каналы + внешние подключения.
 * Ошибки каналов НЕ проглатываются: карточка показывает предупреждение и
 * доступный внутренний чат (данные профиля при этом остаются целыми).
 */
export async function loadContactDetails(contact: ContactSummary, opts: DirectoryOptions): Promise<ContactDetails> {
  const sdk = getSDK();
  const channels: ContactChannel[] = [internalChannel()];
  const warnings: string[] = [];
  const adapterLinksReadable = opts.canReadAdapterLinks || contact.id === opts.myId;

  // Каналы профиля (entity_channels) НЕ являются подключением: значение
  // username/телефона не обеспечивает доставку. Показываем ТОЛЬКО реальные
  // привязки адаптеров (adapter_links) — «написать в Telegram/MAX» доступно
  // ровно там, где мост базы действительно может отправить сообщение.

  if (adapterLinksReadable) {
    try {
      const response = await sdk.adapter.listAdapterLinks({ entityId: contact.id });
      for (const raw of ((response?.items || []) as Record<string, unknown>[])) {
        const channel = adapterChannel(raw);
        if (channel) addChannel(channels, channel);
      }
    } catch (error) {
      warnings.push(`Внешние подключения: ${friendlyError(error)}`);
    }
  }

  // Каналы, уже полученные каталогом (adapter_links админа) — без повторного
  // создания карточек/дублей.
  mergeChannels(channels, contact.channels);

  return {
    ...contact,
    channels,
    channelsError: warnings.join('\n'),
    adapterLinksDenied: !adapterLinksReadable,
  };
}

/**
 * Причина недоступности внешнего действия. Пусто — канал привязан к адаптеру
 * (мост доставит сообщение). Текст — ЧЕСТНАЯ причина для пользователя.
 */
function externalUnavailableHint(details: ContactDetails, kind: ContactChannelKind): string {
  const label = channelLabel(kind);
  if (details.adapterLinksDenied) {
    return `Привязки внешних подключений доступны администратору — подключённый канал ${label} не известен вашей роли.`;
  }
  return `У контакта нет подключённого канала ${label}.`;
}

/**
 * Единый набор действий карточки: только РЕАЛЬНО подключённые внешние каналы
 * (Telegram/MAX из adapter_links), внутренний чат и профиль. Неподключённые
 * внешние кнопки НЕ показываем — это ложное обещание доставки.
 */
export function contactActions(details: ContactDetails): ContactAction[] {
  const external = (kind: 'telegram' | 'max'): ContactAction => {
    const linked = details.channels.some((channel) => channel.kind === kind && channel.source === 'adapter');
    return {
      kind,
      label: channelLabel(kind),
      available: linked,
      hint: linked ? undefined : externalUnavailableHint(details, kind),
    };
  };
  const visible = [external('telegram'), external('max')].filter((action) => action.available);
  return [
    ...visible,
    { kind: 'internal', label: 'Внутренний чат', available: true },
    { kind: 'profile', label: 'Открыть профиль', available: true },
  ];
}

export interface ContactChatResult {
  conversationId: string;
  channel: ContactChannel;
  /** true — у контакта есть привязка адаптера (мост доставит наружу). */
  linkedToAdapter: boolean;
}

/**
 * Открыть переписку с контактом по выбранному каналу.
 * Прямой чат идемпотентен на сервере (повторный вызов вернёт существующий).
 * Внешнему каналу ОБЯЗАТЕЛЬНА привязка адаптера: без неё сообщение осталось
 * бы внутри платформы, хотя пользователь выбрал Telegram/MAX — это ошибка.
 */
export async function openContactChat(
  details: ContactDetails,
  kind: ContactChannelKind,
): Promise<ContactChatResult> {
  const channel = details.channels.find(
    (item) => item.kind === kind && (kind === 'internal' || item.source === 'adapter'),
  );
  if (kind !== 'internal' && !channel) {
    throw new Error(externalUnavailableHint(details, kind));
  }
  const conversation = await createConversation({ kind: 'direct', memberIds: [details.id] });
  return {
    conversationId: conversation.id,
    channel: channel || internalChannel(),
    linkedToAdapter: channel?.source === 'adapter',
  };
}

/** Маршрут профиля пользователя в конфигураторе (только для администратора). */
export function contactProfileRoute(id: string): string {
  return `/designer/users?user=${encodeURIComponent(id)}`;
}
